// components/common/Icons/EntityIcon.tsx
import React from 'react';
import { Box, BoxProps, SxProps, Theme, Typography, TypographyProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SvgIconProps } from '@mui/material/SvgIcon';

type IconComponent = React.ComponentType<SvgIconProps>;

interface EntityIconProps extends SvgIconProps {
    entity: string;
    text?: string | null;
    textProps?: TypographyProps & { sx?: SxProps<Theme> };
    containerProps?: BoxProps;
}

export const EntityIcon = ({ entity, text = null, textProps = {}, containerProps = {}, ...props }: EntityIconProps) => {
    const theme = useTheme();

    if (!entity) {
        console.warn('EntityIcon requires an entity prop');
        return null;
    }

    // Enhanced debugging for deployment issues
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const entityIcons = (theme as any)?.entityIcons as Record<string, IconComponent> | undefined;

    if (!entityIcons) {
        console.error('Theme entityIcons not available - theme loading issue?');
        console.error('Theme keys:', Object.keys(theme || {}));
        return null;
    }

    const IconComponent = entityIcons[entity];

    if (!IconComponent) {
        console.warn(`No icon defined for entity type: "${entity}"`);
        console.warn('Available entity types:', Object.keys(entityIcons));
        console.warn('Theme entityIcons type:', typeof entityIcons);
        console.warn('Entity lookup result:', IconComponent);
        return null;
    }

    // Simple icon only
    if (!text) {
        return <IconComponent data-testid="EntityIcon" {...props} />;
    }

    // Icon with text
    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }} {...containerProps}>
            <IconComponent
                data-testid="EntityIcon"
                sx={{ color: 'primary.main', fontSize: 18, mr: 1, flexShrink: 0, ...(props.sx || {}) }}
                {...props}
            />
            <Typography variant="body2" sx={{ color: 'text.primary', ...(textProps.sx || {}) }} {...textProps}>
                {text}
            </Typography>
        </Box>
    );
};
